// TESSERAX — RANG DE FIN DE PARTIE (cœur pur, testé).
//
// Chaque run se conclut sur une NOTE de maîtrise S..D — pas un simple « tu as perdu »,
// mais un verdict lisible qui donne un objectif immédiat (« passer A → S ») et relance
// la partie suivante. C'est le crochet d'addiction classique des grands jeux d'arcade :
// un score brut ne dit rien au joueur ; une lettre, si.
//
// Le rang juge la MAÎTRISE, pas la seule survie ni la durée : il mesure la DENSITÉ de jeu
// (points par ligne, normalisés par la profondeur pour rester équitables entre un sprint et
// une partie longue) ET les feats de skill discrets — plus longue série/résonance, plus long
// CONTINUUM (back-to-back), CROIX (le feat propre au puits 3D) et PERFECTS. Un grinder qui
// n'efface que des lignes simples plafonne ; un joueur qui empile étages, croix et continuums
// grimpe. Chaque composante est BORNÉE et MONOTONE → ajouter un feat ne peut jamais baisser le
// rang (propriété verrouillée par les tests), et aucune composante seule ne suffit à décrocher S.
//
// Module PUR : ni THREE ni DOM, pas de Math.random → déterministe, donc affichable sur l'écran
// de fin ET reproductible sur la carte de partage / un futur fantôme du défi du jour.

export type Grade = 'S' | 'A' | 'B' | 'C' | 'D';

// Résumé d'une partie, alimenté par main.ts à partir des compteurs déjà suivis (recordRun).
export interface RunSummary {
  score: number;       // score final
  lines: number;       // alignements effacés
  level: number;       // profondeur max atteinte
  series: number;      // plus longue série / résonance (combo enchaîné)
  continuum: number;   // plus long CONTINUUM (clears de maîtrise d'affilée)
  cross: number;       // nombre de CROIX (deux axes horizontaux d'un coup)
  perfects: number;    // nombre de puits vidés (perfect clear)
}

export interface RankResult {
  grade: Grade;        // S | A | B | C | D
  points: number;      // indice de maîtrise 0..100 (arrondi)
  toNext: number;      // points manquants pour le rang supérieur (0 à S)
  next: Grade | null;  // rang supérieur visé (null à S)
}

// Seuils de l'indice de maîtrise → lettre. Conservateurs : S exige À LA FOIS une forte densité
// ET plusieurs feats ; un alignement simple sans combo retombe en C/D. Réglage « feel » volontairement
// prudent (livrable en aveugle) — un ajustement fin reste un pari à revue humaine (cf. ROADMAP).
export const GRADE_THRESHOLDS: ReadonlyArray<{ grade: Grade; min: number }> = [
  { grade: 'S', min: 80 },
  { grade: 'A', min: 62 },
  { grade: 'B', min: 42 },
  { grade: 'C', min: 22 },
  { grade: 'D', min: 0 },
];

function clamp01(x: number): number {
  return x < 0 ? 0 : x > 1 ? 1 : x;
}

// Indice de maîtrise 0..100, somme de contributions bornées (donc monotone en chaque entrée).
// Pondérations : la densité de jeu pèse le plus (45), puis les feats de skill (55 cumulés), de
// sorte qu'aucun levier isolé ne « cheese » le rang S.
export function masteryPoints(r: RunSummary): number {
  if (!r || r.score <= 0 || r.lines <= 0) return 0;
  const level = Math.max(1, r.level);
  // EFFICACITÉ : points par ligne, normalisés par la profondeur. Le clear trivial vaut ~30·level,
  // donc score/lignes/level ≈ 30 pour un jeu sans relief ; combos, étages, croix et continuums le
  // poussent bien au-delà. On mappe [30 → 200] sur [0 → 45] (au-dessus de 200 : plafonné).
  const effPerLine = r.score / r.lines / level;
  const eff = clamp01((effPerLine - 30) / 170) * 45;
  // FEATS de skill discrets, chacun borné.
  const series = clamp01(Math.max(0, r.series) / 12) * 20;       // série/résonance soutenue
  const continuum = clamp01(Math.max(0, r.continuum) / 10) * 15; // back-to-back de maîtrise
  const cross = clamp01(Math.max(0, r.cross) / 8) * 12;          // skill 3D signature
  const perfects = clamp01(Math.max(0, r.perfects) / 3) * 8;     // puits vidés
  return Math.round(eff + series + continuum + cross + perfects);
}

// Note finale + distance au rang supérieur (pour un HUD « +X → A »).
export function rankRun(r: RunSummary): RankResult {
  const points = masteryPoints(r);
  let grade: Grade = 'D';
  for (const tier of GRADE_THRESHOLDS) {
    if (points >= tier.min) { grade = tier.grade; break; }
  }
  // Rang supérieur = la première entrée d'indice plus bas dans la table (S est en tête).
  const idx = GRADE_THRESHOLDS.findIndex((t) => t.grade === grade);
  const upper = idx > 0 ? GRADE_THRESHOLDS[idx - 1] : null;
  const toNext = upper ? Math.max(0, upper.min - points) : 0;
  return { grade, points, toNext, next: upper ? upper.grade : null };
}
